import { Suspense } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import SiteHeader from "./SiteHeader";
import SiteFooter from "./SiteFooter";
import { AppSettings } from "../account-kit";
import PageLoading from "./PageLoading";
import ErrorBoundary from "@jkast90/kast-common/error-boundary";
import { AppShell, Banner, buttonClass } from "@jkast90/kast-common/ui";
import { logger } from "@jkast90/kast-common/diagnostics";
import { useRecordNavigation } from "@jkast90/kast-common/diagnostics/react";
import styles from "./SiteLayout.module.css";

const BUTTON = buttonClass({ variant: "secondary" });
const PRIMARY_BUTTON = buttonClass({ variant: "primary" });

/**
 * The shell every page renders in. A crash inside a page is caught below the
 * header, so the nav stays usable. The boundary is keyed on the route's
 * section (the first path segment), so moving to another section clears a
 * crash, but switching admin tabs (/admin/:tab) doesn't remount the page.
 */
/** The whole path is the screen, so the shop and admin pages show in the report. */
const wholePath = (pathname: string) => pathname;

export default function SiteLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const section = location.pathname.split("/")[1];

  // The screens leading up to a bug report. Off the location, so deep links
  // and back/forward count too.
  useRecordNavigation(location.pathname, wholePath);

  return (
    <>
      <AppShell
        header={
          <>
            <SiteHeader />
            <div className={styles.banners}>
              <Banner />
            </div>
          </>
        }
        footer={<SiteFooter />}
      >
        <ErrorBoundary
          key={section}
          onError={(error, info) => logger.error(`[Crash] ${error.message}`, info.componentStack ?? "")}
          onReport={(crashNote) => navigate("/bug-report", { state: { crashNote } })}
          buttonClassName={BUTTON}
          primaryButtonClassName={PRIMARY_BUTTON}
        >
          <Suspense fallback={<PageLoading />}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      </AppShell>
      <AppSettings />
    </>
  );
}
